import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ArrowUpRightIcon from "@redotech/redo-icons/arrow-up-right_filled.svg";
import CoinsHandIcon from "@redotech/redo-icons/coins-hand.svg";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import ReturnIcon from "@redotech/redo-icons/corner-up-left_filled.svg";
import DotsHorizontalIcon from "@redotech/redo-icons/dots-horizontal.svg";
import EditIcon from "@redotech/redo-icons/edit-01.svg";
import ShieldPlusIcon from "@redotech/redo-icons/shield-plus.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { updateOrder } from "@redotech/redo-merchant-app-common/client/shopify";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { CancelOrderModal } from "@redotech/redo-merchant-support-app/action-panel/cancel-order-modal";
import { RefundOrderModal } from "@redotech/redo-merchant-support-app/action-panel/refund-order-modal";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { EditOrderModal } from "@redotech/redo-web/edit-order-modal";
import { Flex } from "@redotech/redo-web/flex";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DuplicateOrderModal } from "../../order/action-panel/duplicate-order-modal";
import { MerchantCreateReturnModal } from "../../return/claim-return-shared";
import type { OrderWithPickStatus } from "../db/collections";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import {
  getAvailableSupportV2OrderActions,
  type SupportV2OrderAction,
} from "./order-card-action-utils";

export interface OrderCardActionsProps {
  order: OrderWithPickStatus;
}

type ActiveOrderActionModal = Exclude<SupportV2OrderAction, "viewInRedo">;

export const OrderCardActions = memo(function OrderCardActions({
  order,
}: OrderCardActionsProps) {
  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const collections = useGlobalSupportTicketCollections();
  const navigate = useNavigate();
  const location = useLocation();
  const hasEditOrderPermission = useHasPermission(
    OrderManagementPermissions.EDIT_ORDER,
  );
  const canViewAllOrders = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER,
  );
  const canEditReturn = useHasPermission(
    OrderManagementPermissions.EDIT_RETURN,
  );

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [activeModal, setActiveModal] = useState<ActiveOrderActionModal | null>(
    null,
  );
  const [createType, setCreateType] = useState<ReturnTypeEnum>(
    ReturnTypeEnum.RETURN,
  );

  const canEditOrder =
    order.provider === "commentsold" ? false : hasEditOrderPermission;

  const searchParams = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search).entries()),
    [location.search],
  );

  const currentView = useMemo(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    return pathSegments[pathSegments.length - 1];
  }, [location.pathname]);

  const availableActions = useMemo(
    () =>
      getAvailableSupportV2OrderActions({
        order,
        returnsEnabled: team.settings.returns?.enabled,
        packageProtectionEnabled: team.settings.packageProtection?.enabled,
        canViewAllOrders,
        canEditOrder,
        canEditReturn,
      }),
    [
      canEditOrder,
      canEditReturn,
      canViewAllOrders,
      order,
      team.settings.packageProtection?.enabled,
      team.settings.returns?.enabled,
    ],
  );

  const handleToggleMenu = useHandler(() => {
    setMenuOpen((open) => !open);
  });

  const handleOrdersChange = useHandler(() => {
    void collections.orders.utils.refetch();
  });

  const handleCreateReturn = useHandler(() => {
    amplitude.logEvent("view-createReturnModal", {
      shopifyOrderId: order.shopify_id,
    });
    setCreateType(ReturnTypeEnum.RETURN);
    setActiveModal("createReturn");
  });

  const handleCreateClaim = useHandler(() => {
    amplitude.logEvent("view-createClaimModal", {
      shopifyOrderId: order.shopify_id,
    });
    setCreateType(ReturnTypeEnum.CLAIM);
    setActiveModal("createClaim");
  });

  const handleEditOrder = useHandler(() => {
    amplitude.logEvent("view-updateOrderModal", {
      shopifyOrderId: order.shopify_id,
    });
    setActiveModal("editOrder");
  });

  const handleDuplicateOrder = useHandler(() => {
    amplitude.logEvent("view-duplicateOrderModal", {
      shopifyOrderId: order.shopify_id,
    });
    setActiveModal("duplicateOrder");
  });

  const handleCancelOrder = useHandler(() => {
    amplitude.logEvent("view-cancelOrderModal", {
      shopifyOrderId: order.shopify_id,
    });
    setActiveModal("cancelOrder");
  });

  const handleRefundOrder = useHandler(() => {
    amplitude.logEvent("view-refundOrderModal", {
      shopifyOrderId: order.shopify_id,
    });
    setActiveModal("refundOrder");
  });

  const handleViewInRedo = useHandler(() => {
    amplitude.logEvent("view-orderInRedo", {
      shopifyOrderId: order.shopify_id,
    });
    navigate(
      `/stores/${team._id}/shipping/outbound-labels/orders/${order._id}`,
    );
  });

  const setCreateReturnModalOpen = useHandler((open: boolean) => {
    if (!open) {
      setActiveModal(null);
    }
  });

  const setEditOrderModalOpen = useHandler((open: boolean) => {
    setActiveModal(open ? "editOrder" : null);
  });

  const setCancelOrderModalOpen = useHandler((open: boolean) => {
    setActiveModal(open ? "cancelOrder" : null);
  });

  const setDuplicateOrderModalOpen = useHandler((open: boolean) => {
    setActiveModal(open ? "duplicateOrder" : null);
  });

  const setRefundOrderModalOpen = useHandler((open: boolean) => {
    setActiveModal(open ? "refundOrder" : null);
  });

  const menuItems = useMemo(
    () =>
      availableActions.map((action): RedoCommandMenuItem => {
        switch (action) {
          case "editOrder":
            return {
              Icon: EditIcon,
              text: "Edit order",
              onClick: handleEditOrder,
            };
          case "cancelOrder":
            return {
              Icon: XCloseIcon,
              text: "Cancel",
              onClick: handleCancelOrder,
            };
          case "createReturn":
            return {
              Icon: ReturnIcon,
              text: "Create return",
              onClick: handleCreateReturn,
            };
          case "createClaim":
            return {
              Icon: ShieldPlusIcon,
              text: "Create claim",
              onClick: handleCreateClaim,
            };
          case "duplicateOrder":
            return {
              Icon: CopyIcon,
              text: "Duplicate",
              onClick: handleDuplicateOrder,
            };
          case "refundOrder":
            return {
              Icon: CoinsHandIcon,
              text: "Refund",
              onClick: handleRefundOrder,
            };
          case "viewInRedo":
            return {
              Icon: ArrowUpRightIcon,
              text: "View in Redo",
              onClick: handleViewInRedo,
            };
          default:
            assertNever(action);
        }
      }),
    [
      availableActions,
      handleCancelOrder,
      handleCreateClaim,
      handleCreateReturn,
      handleDuplicateOrder,
      handleEditOrder,
      handleRefundOrder,
      handleViewInRedo,
    ],
  );

  if (menuItems.length === 0) {
    return null;
  }

  const createReturnModalOpen =
    activeModal === "createReturn" || activeModal === "createClaim";

  return (
    <>
      <Flex>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={DotsHorizontalIcon}
          onClick={handleToggleMenu}
          ref={setMenuAnchor}
          size="xs"
        />
        <RedoCommandMenu
          anchor={menuAnchor}
          items={menuItems}
          open={menuOpen}
          placement="bottom-end"
          setOpen={setMenuOpen}
        />
      </Flex>
      {createReturnModalOpen && canEditReturn && (
        <MerchantCreateReturnModal
          open={createReturnModalOpen}
          relocationInfo={{
            preselectedOrder: order,
            currentView,
            returnQueryParams:
              createType === ReturnTypeEnum.RETURN ? searchParams : undefined,
          }}
          returnType={createType}
          setOpen={setCreateReturnModalOpen}
        />
      )}
      {activeModal === "editOrder" && (
        <EditOrderModal
          onOrdersChange={handleOrdersChange}
          open
          order={order}
          setOpen={setEditOrderModalOpen}
          updateOrder={(data) => updateOrder(client, data)}
        />
      )}
      {activeModal === "cancelOrder" && (
        <CancelOrderModal
          onOrdersChange={handleOrdersChange}
          open
          order={order}
          setOpen={setCancelOrderModalOpen}
        />
      )}
      {activeModal === "duplicateOrder" && (
        <DuplicateOrderModal
          open
          order={order}
          setOpen={setDuplicateOrderModalOpen}
        />
      )}
      {activeModal === "refundOrder" && (
        <RefundOrderModal
          onOrdersChange={handleOrdersChange}
          open
          order={order}
          setOpen={setRefundOrderModalOpen}
        />
      )}
    </>
  );
});
